<script setup>
import { ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import { XMarkIcon, UserIcon } from '@heroicons/vue/20/solid';
import { useIntersectionObserver } from '@vueuse/core';
import JetDialogModal from '@/Components/DialogModal.vue';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';

const emit = defineEmits(['closed']);

const scrollEnd = ref(null);
const showScrollIndicator = ref(true);

defineProps({
    contributor: {
        type: Object,
        required: true,
    },
    show: {
        type: Boolean,
        default: false,
    },
});

useIntersectionObserver(
    scrollEnd,
    ([{ isIntersecting }]) => {
        showScrollIndicator.value = !isIntersecting;
    }, {
        threshold: 1,
    },
);
</script>

<template>
    <JetDialogModal
        :show
        :hide-footer="true"
        @close="emit('closed')"
    >
        <template #title>
            <div class="border-b pb-8">
                <div class="flex justify-between items-start">
                    <div class="flex items-center space-x-6">
                        <picture
                            v-if="contributor.profile.image"
                            class="shrink-0"
                        >
                            <source
                                :srcset="contributor.profile.image.glide_avatar_image_url_avif"
                                type="image/avif"
                            >
                            <source
                                :srcset="contributor.profile.image.glide_avatar_image_url_webp"
                                type="image/webp"
                            >
                            <img
                                class="h-20 w-20 object-cover rounded-full"
                                :src="contributor.profile.image.glide_avatar_image_url_jpg"
                                :alt="`${$t('eventDetails.contributor.image.alt')} ${contributor.profile.fullname}`"
                                loading="lazy"
                            >
                        </picture>
                        <div
                            v-else
                            class="flex items-center justify-center shrink-0 h-20 w-20 bg-white rounded-full border"
                            aria-hidden="true"
                        >
                            <UserIcon class="h-12 w-12 text-gray-600" />
                        </div>

                        <div class="space-y-0.5 md:space-y-1">
                            <div
                                class="font-semibold text-lg text-deep-teal-700 leading-tight"
                            >
                                {{ contributor.profile.display_name }}
                            </div>

                            <div class="font-light text-sm/tight text-gray-700">
                                {{ contributor.profile.company }}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        :title="$t('eventDetails.actions.closeContributorModal')"
                        @click="emit('closed')"
                    >
                        <span class="sr-only">
                            {{ $t('eventDetails.actions.closeContributorModal') }}
                        </span>

                        <XMarkIcon
                            class="h-5 w-5 text-gray-500 hover:text-gray-800 focus:text-gray-800"
                            aria-hidden="true"
                        />
                    </button>
                </div>

                <div class="flex justify-end">
                    <Link
                        v-if="contributor.profile.slug"
                        :href="route('contributor-profile.show', { contributorProfile: contributor.profile.slug })"
                        class="inline-flex text-base group"
                        @click="emit('closed')"
                    >
                        <span class="flex items-start">
                            <span class="mr-1.5 font-semibold text-deep-teal-700 group-hover:underline">
                                {{ $t('eventDetails.contributor.url') }}
                            </span>

                            <span class="relative flex items-center justify-center flex-shrink-0 w-6 h-6">
                                <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="w-full h-full text-deep-teal-700"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                    />
                                </svg>
                            </span>
                        </span>
                    </Link>
                </div>
            </div>
        </template>

        <template #content>
            <div
                class="relative space-y-8 h-96 max-h-full overflow-y-auto overflow-x-hidden"
            >
                <div
                    v-if="contributor.profile.vita"
                    class="space-y-2"
                >
                    <h3 class="font-semibold text-xl text-deep-teal-700">
                        {{ $t('eventDetails.headline.vita') }}
                    </h3>

                    <div
                        class="prose 2xl:prose-lg max-w-none"
                        v-html="contributor.profile.vita"
                    />
                </div>

                <div
                    v-if="contributor.profile.program_packages && contributor.profile.program_packages.length > 0"
                    class="space-y-2"
                >
                    <h3 class="font-semibold text-xl text-deep-teal-700">
                        {{ $t('eventDetails.headline.alreadyContributedTo') }}
                    </h3>

                    <ul class="space-y-1">
                        <li
                            v-for="(programPackage, index) in contributor.profile.program_packages"
                            :key="index"
                        >
                            <Link
                                as="a"
                                :href="programPackage.url"
                                class="text-deep-teal-700 hover:text-deep-teal-900 focus:text-deep-teal-900 hover:underline focus:underline"
                            >
                                {{ programPackage.title }}
                            </Link>

                            <time
                                class="block font-light text-sm text-gray-600"
                                :datetime="programPackage.startDate"
                            >
                                {{ useGetLocalizedDateTime(programPackage.start_date) }}
                            </time>
                        </li>
                    </ul>
                </div>

                <div
                    ref="scrollEnd"
                    class="!-mt-8 invisible"
                />

                <transition
                    enter-from-class="opacity-0"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="showScrollIndicator"
                        class="fixed bottom-0 w-full h-20 bg-[linear-gradient(0deg,_white_25%,_transparent_100%)] transition-opacity duration-300"
                    />
                </transition>
            </div>
        </template>
    </JetDialogModal>
</template>
